import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
  differenceInMonths,
  isSameMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  endOfDay,
  format,
  formatISO,
  isAfter,
  startOfDay,
  subDays,
} from 'date-fns';
import { Link } from 'react-router-dom';
import Report from '../../../../componentsV2/Layout/Report';
import { addLoadingList, removeLoadingList, setLoadingDescription } from '../../../../reducers/notification';
import SalesExpandRow from './components/SalesExpandRow';
import useSearchParams from '../../../../hooks/useSearchParams';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';
import { useReport } from '../../../../context/ReportContext';
import moment from 'moment';

const Sales = ({ auth }) => {
  // Web Worker initialization
  const workerRef = useRef(null);
  const [processedData, setProcessedData] = useState({
    dataBySales: [],
    dataByClientAndDate: [],
    dataByClient: [],
    stats: [],
    graphData: {},
    totalCount: 0,
  });

  const options = useMemo(
    () => [
      {
        title: 'Activity Type',
        key: '0-0',
        value: 'activity',
        children: [
          {
            key: '0-0-0',
            title: 'Message',
            value: 'chatMessages',
          },
          {
            key: '0-0-1',
            title: 'AI Insight',
            value: 'aiActivityResult',
          },
          {
            key: '0-0-3',
            title: 'Client Activity',
            value: 'clientActivity',
          },
          {
            key: '0-0-2',
            title: 'Reminder',
            value: 'reminder',
          },
          {
            key: '0-0-4',
            title: 'Appointment',
            value: 'appointments',
          },
        ],
      },
    ],
    [],
  );
  const [filters, setFilters] = useState({
    activity: [],
    store: [],
    associate: [],
  });

  const { setValue: setSearchParams, getAll } = useSearchParams();
  const params = getAll();

  // Memoize auth values to prevent unnecessary worker reprocessing
  const authStores = useMemo(() => auth.stores, [auth.stores]);

  // Initialize Web Worker
  useEffect(() => {
    workerRef.current = new Worker(new URL('./salesWorker.js', import.meta.url));

    workerRef.current.onmessage = (e) => {
      if (e.data.type === 'DATA_PROCESSED') {
        setProcessedData(e.data.payload);
        removeLoadingList('salesReport');
        setLoadingDescription(null);
      } else if (e.data.type === 'PROCESSING_ERROR') {
        console.error('Worker processing error:', e.data.error, e.data.stack);
        removeLoadingList('salesReport');
        setLoadingDescription(null);
      }
    };

    return () => workerRef.current?.terminate();
  }, []);

  useEffect(() => {
    const paramsFilters = {
      activity: params?.activity ? params?.activity : [],
      store: params?.store ? params?.store : [],
      associate: params?.associate ? params?.associate : [],
    };
    setFilters(paramsFilters);
    if (params?.startDate && params?.endDate) {
      setDateRange([new Date(params.startDate), new Date(params.endDate)]);
    } else {
      setDateRange([startOfDay(subDays(new Date(), 90)), endOfDay(new Date())]);
    }
  }, [window.location.search]);

  const [dateRange, setDateRange] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSizeTable, setPageSizeTable] = useState(10);
  const [page, setPage] = useState(1);

  const { chatMessages, isLoadingChatMessage, reminders, isLoadingReminder, clientActivity, isLoadingClientActivity, appointments, isLoadingAppointments } =
    useReport();

  const getDefaultPeriod = useMemo(() => {
    if (params?.startDate && params?.endDate) {
      if (
        isSameMonth(new Date(params.endDate), new Date(params.startDate)) &&
        isFirstDayOfMonth(new Date(params.startDate)) &&
        isLastDayOfMonth(new Date(params.endDate)) &&
        differenceInMonths(new Date(), new Date(params.startDate)) === 1
      ) {
        return 'previousmonth';
      }
      return `${format(new Date(params.startDate), 'MM/dd/yyyy')} - ${format(new Date(params.endDate), 'MM/dd/yyyy')}`;
    }
    return 'previousmonth';
  }, []);

  const handleOnDateRangeChange = useCallback(dates => {
    // Start loading spinner immediately
    setLoadingDescription("Sit tight! We're compiling your report.");
    addLoadingList('salesReport');

    setSearchParams('startDate', formatISO(dates[0]));
    setSearchParams('endDate', formatISO(dates[1]));
  }, []);

  useEffect(() => {
    if (isLoadingChatMessage || isLoadingReminder || isLoadingAppointments) {
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('salesReport');
    } else {
      removeLoadingList('salesReport');
      setLoadingDescription(null);
    }
  }, [isLoadingChatMessage, isLoadingReminder, isLoadingClientActivity, isLoadingAppointments]);

  // Trigger data processing in Web Worker
  useEffect(() => {
    if (!chatMessages || !reminders || !clientActivity || !appointments ||
        isLoadingChatMessage || isLoadingReminder || isLoadingClientActivity || isLoadingAppointments) {
      return;
    }

    // Ensure worker is initialized
    if (!workerRef.current) {
      console.error('Worker not initialized');
      return;
    }

    setLoadingDescription("Sit tight! We're compiling your report.");
    addLoadingList('salesReport');
    setPage(1); // Reset page when data changes

    try {
      workerRef.current.postMessage({
        type: 'PROCESS_DATA',
        payload: {
          chatMessages,
          reminders,
          clientActivity,
          appointments,
          filters,
          dateRange,
          assocStores: authStores,
        },
      });
    } catch (error) {
      console.error('Error posting to worker:', error);
      removeLoadingList('salesReport');
      setLoadingDescription(null);
    }
  }, [chatMessages, reminders, clientActivity, appointments, filters, dateRange, authStores, isLoadingChatMessage, isLoadingReminder, isLoadingClientActivity, isLoadingAppointments]);

  const handleFilterChange = useCallback(value => {
    // Start loading spinner immediately
    setLoadingDescription("Sit tight! We're compiling your report.");
    addLoadingList('salesReport');

    const updatedFilters = {
      activity: [],
      store: [],
      associate: [],
    };
    value.forEach(item => {
      if (item[0] && item[1]) {
        updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
      } else {
        updatedFilters[item[0]] = [];
      }
    });
    Object.keys(updatedFilters).forEach(key => {
      setSearchParams(key, updatedFilters[key].join(','));
    });
  }, []);

  const removeFilter = useCallback(
    (key, value) => {
      // Start loading spinner immediately
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('salesReport');

      const updatedFilters = { ...filters };
      updatedFilters[key] = updatedFilters[key].filter(filter => filter !== value);
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  // Convert worker stats format back to JSX for rendering
  const stats = useMemo(() => {
    if (!processedData.stats || processedData.stats.length === 0) {
      return [];
    }
    return processedData.stats.map(stat => ({
      ...stat,
      description: <span className={stat.description.className}>{stat.description.text}</span>,
    }));
  }, [processedData.stats]);

  // Use graphData from worker
  const graphData = processedData.graphData;

  const columns = useMemo(() => {
    const storesColumn =
      auth?.stores?.filter(store => store.id !== 0).length > 1
        ? [
          {
            title: 'Store',
            dataIndex: 'store_name',
            key: 'store_name',
            sorter: (a, b) => a.store_name.localeCompare(b.store_name),
          },
        ]
        : [];
    return [
      {
        title: 'Date',
        dataIndex: 'sales_transaction_date',
        key: 'sales_transaction_date',
        sorter: (a, b) => (isAfter(new Date(a.sales_transaction_date), new Date(b.sales_transaction_date)) ? 1 : -1),
        render: value => (value ? moment.utc(value).format('MMM DD, yyyy') : ' - '),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Client',
        dataIndex: 'client_name',
        key: 'client_name',
        sorter: (a, b) => a.client_name.localeCompare(b.client_name),
        render: (_, row) => <Link to={`/Clients?client=${row.client_id}`}>{row.client_name}</Link>,
      },
      ...storesColumn,
      {
        title: 'Amount',
        dataIndex: 'sales_transaction_amount',
        key: 'sales_transaction_amount',
        sorter: (a, b) => a.sales_transaction_amount - b.sales_transaction_amount,
        render: value => formatNumberAsCurrency(value || 0),
      },
    ];
  }, [auth.stores]);

  const expandedRowRender = useCallback(record => {
    return <SalesExpandRow {...record} licenseKey={auth.licenseKey} />;
  }, []);

  const handleExpandRow = useCallback(
    (expand, record) => {
      let updatedExpandedRowKeys = [...expandedRowKeys];
      if (expand) {
        updatedExpandedRowKeys.push(record.client_store_date_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      } else {
        updatedExpandedRowKeys = updatedExpandedRowKeys.filter(key => key !== record.client_store_date_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      }
    },
    [expandedRowKeys],
  );

  const loadingGraph = useMemo(() => {
    if (reminders && chatMessages && clientActivity && appointments) {
      return reminders?.reportData?.length + chatMessages?.reportData?.length + clientActivity?.reportData?.length + appointments?.reportData?.length >
        0
        ? !(Object.keys(graphData).length > 0)
        : true;
    }
    return true;
  }, [reminders, chatMessages, clientActivity, appointments, graphData]);

  return (
    <Report
      title="Sales"
      stats={stats}
      options={options}
      hasCSVExport
      CSVFilename="sales.csv"
      handleFilterChange={handleFilterChange}
      filters={filters}
      removeFilter={removeFilter}
      onDateRangeChange={handleOnDateRangeChange}
      dataRange={dateRange}
      defaultPeriodValue={getDefaultPeriod}
      graphData={
        !isLoadingClientActivity && !isLoadingReminder && !isLoadingChatMessage && !isLoadingAppointments && !loadingGraph ? graphData : null
      }
      columns={columns}
      table={processedData.dataByClientAndDate}
      stacked
      tablesProps={{
        pagination: {
          total: processedData.dataByClientAndDate.length,
          showSizeChanger: true,
          showTotal: total => `Total ${total} items`,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSizeTable(pageSize);
          },
          page,
          pageSize: pageSizeTable,
        },
        rowKey: 'client_store_date_id',
        expandable: {
          expandedRowRender: record => expandedRowRender(record),
          defaultExpandedRowKeys: ['0'],
          onExpand: (expanded, record) => handleExpandRow(expanded, record),
          expandedRowKeys,
          rowExpandable: record => record.activitiesLength > 0,
          expandRowByClick: true,
        },
      }}
      maxTicksLimit={8}
      labelLines={Object.keys(graphData).length === 0 ? 1 : Math.ceil(Object.keys(graphData).length / 9)}
    />
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(Sales);
